const SAVE_DEBOUNCE_MS = 750;

type PendingDebounce = {
  supersede: () => void;
};

const debounces = new Map<string, PendingDebounce>();
const queues = new Map<string, Promise<unknown>>();

// customization patches arrive far faster than they can be saved - the subject field patches on
// every keystroke - so the write waits for the edits to go quiet. A newer patch for the same email
// supersedes the one waiting, because the store already holds every patch and the winning save
// reads the email back out of it.
function waitForQuietPeriod(uuid: string): Promise<boolean> {
  debounces.get(uuid)?.supersede();

  return new Promise<boolean>((resolve) => {
    const timer = setTimeout(() => {
      debounces.delete(uuid);
      resolve(true);
    }, SAVE_DEBOUNCE_MS);

    debounces.set(uuid, {
      supersede: () => {
        clearTimeout(timer);
        resolve(false);
      },
    });
  });
}

/**
 * Debounces and serializes background saves for a single email. Resolves `false` when a newer patch
 * superseded this one (nothing was sent), `true` once the save has landed. Rejects if the save
 * itself failed.
 */
export async function scheduleCustomizationsSave(uuid: string, save: () => Promise<void>): Promise<boolean> {
  const ourTurn = await waitForQuietPeriod(uuid);
  if (!ourTurn) return false;

  // a slow request can still be in flight when the next patch settles - chaining keeps the writes
  // for an email in dispatch order so the last patch is what the database ends up with
  const previous = queues.get(uuid) ?? Promise.resolve();
  const next = previous.catch(() => undefined).then(save);
  queues.set(uuid, next);

  try {
    await next;
    return true;
  } finally {
    if (queues.get(uuid) === next) queues.delete(uuid);
  }
}
